import FormElement from "rc-field-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/Button";
import Content from "./Content";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import { useIsMasterCompany } from "../../../../../utils/hooks/company";
import { useLocation, useSearch } from "../helpers";
import { useModuleAccess } from "../../../../../utils/hooks/access";
import { useIsFieldDisabled } from "../../../helpers/useIsFieldDisabled";

const formatSubbmittedFields = (values) => ({
	...values,
	country: values?.country?.id,
	state: values?.state,
	city: values?.city,
	timezone: values?.timezone?.id,
	level1: values?.level1?.map((level) => level?.id),
	level2: values?.level2?.map((level) => level?.id),
	level3: values?.level3?.map((level) => level?.id),
	level4: values?.level4?.map((level) => level?.id),
});

export const defaultCenter = { lat: 40.6974034, lng: -74.1197633 };
export const defaultZoom = 11;

function Form({ mode, values, error, loading, submit, close }) {
	const { t } = useTranslation();
	const [form] = FormElement.useForm();
	const isMasterCompany = useIsMasterCompany();
	const { access } = useModuleAccess("settings.general.locations");
	const disabled = useIsFieldDisabled({ ...access, mode });
	const [markerError, setMarkerError] = useState(false);


	const coords = useMemo(() => {
		const validCoord = (values?.location?.coordinates || []).filter(
			(c) => Array.isArray(c) && c.every((l) => l !== null),
		);
		if (validCoord.length) {
			const coordinates = validCoord[0]?.map((coordinate) => {
				return {
					lat: coordinate[1],
					lng: coordinate[0],
				};
			});

			return coordinates?.slice(0, -1);
		}
		return undefined;

	}, [values?.location?.coordinates]);

	const [coordinates, setCoordinates] = useState(coords);

	const {
		marker,
		setMarker,
		zoom,
		setZoom,
		center,
		setCenter,
		changeMarker,
	} = useLocation(form);


	const { search, setSearch, handleSelect } = useSearch(
		changeMarker,
		setCoordinates,
	);

	const onClose = useCallback(() => {
		form.resetFields();
		close();
	}, [form, close]);

	const formattedCoords = useMemo(() => {
		let coords = [];
		if (Array.isArray(coordinates?.[0]?.[0])) {
			return coordinates[0];
		} else {
			coordinates?.forEach(({ lat, lng }) => coords.push([lng, lat]));
		}
		return coords;
	}, [coordinates]);

	const onFinish = useCallback(
		(formValues) => {
			const data = {
				...formatSubbmittedFields(formValues),
				searchValue: search,
				mapCenter: center,
				mapZoom: zoom,
				marker,
				companies: formValues?.companies?.map(({ id }) => id) || [],
				payGroups: formValues?.payGroups?.map(({ id }) => id) || [],
				unions: formValues?.unions?.map(({ id }) => id) || [],
				location: {
					type: "Polygon",
					coordinates: [[...formattedCoords, formattedCoords[0]]],
				},
			};
			if(!marker?.lat && !marker?.lng) {
				setMarkerError(true);
			} else {
				setMarkerError(false);
				submit(data);
			}
		},
		[submit, marker, zoom, center, formattedCoords, search],
	);

	const onManageAbilityChange = useCallback(() => {
		form.setFieldsValue({
			companies: undefined,
			payGroups: undefined,
			unions: undefined,
		})
	}, [form])

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			status: "active",
			...values,
			manageAbility: values?.manageAbility || "all",
			companies: values?.companies,
			payGroups: values?.payGroups,
			unions: values?.unions,
		});
		setCenter(values?.mapCenter || defaultCenter);
		setZoom(values?.mapZoom || defaultZoom);
		setMarker(values?.marker || null);
		setCoordinates(coords);
	}, [form, values, setCenter, setZoom, setMarker, setCoordinates, coords]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			["code", "description", "status", "country"],
			error,
		);

		form.setFields(fieldErrors);
	}, [error, form]);

	return (
		<FormElement
			form={form}
			className="d-flex flex-column justify-content-between h-100"
			onFinish={onFinish}
		>
			<div>
				<Content
					mode={mode}
					marker={marker}
					markerError={markerError}
					zoom={zoom}
					center={center}
					disabled={disabled || !isMasterCompany}
					onManageAbilityChange={onManageAbilityChange}
					search={search}
					setSearch={setSearch}
					handleSelect={handleSelect}
					coords={coords}
					setCoordinates={setCoordinates}
					setZoom={setZoom}
					setCenter={setCenter}
				/>
			</div>

			<div className="d-flex justify-content-end">
				<Button
					onClick={onClose}
					disabled={loading}
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
				>
					{t("cancel")}
				</Button>

				{(!disabled && isMasterCompany) && (
					<Button
						type="submit"
						className="btn-dark btn-sm shadow-none"
						loading={loading}
					>
						{t("save")}
					</Button>
				)}
			</div>
		</FormElement>
	);
}

export default Form;
